import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Layers, Pencil, Plus, Settings2, Trash2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  PageContainer,
  PageHeader,
  PageSkeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  SimpleSelect,
} from "@bs/ui";
import { client, orpc } from "../lib/orpc.ts";

type Tab = "published" | "draft" | "archived";
const TABS: ReadonlyArray<{ key: Tab; label: string; empty: string }> = [
  { key: "published", label: "Published", empty: "No published themes. Publish a draft to offer it to stores." },
  { key: "draft", label: "Drafts", empty: "No drafts. Use New theme to start building one." },
  { key: "archived", label: "Archive", empty: "Nothing archived. Archiving a published theme moves it here and hides it from stores." },
];

const INDUSTRIES = ["general", "fashion", "food", "beauty", "electronics", "home", "other"];

export function Templates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: templates, isLoading, isError, error } = useQuery(orpc.templates.list.queryOptions());
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [cloneFrom, setCloneFrom] = useState("");
  const [industry, setIndustry] = useState("general");
  const [description, setDescription] = useState("");
  // Library card details of an existing theme (name, description, industry).
  const [editing, setEditing] = useState<{ code: string; name: string; description: string; industry: string } | null>(null);

  const [tab, setTab] = useState<Tab>("published");
  const [deleting, setDeleting] = useState<{ code: string; name: string } | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.templates.list.key() });
  const fail = (what: string) => (err: Error) => toast.error(`Could not ${what}: ${err.message}`);

  const create = useMutation({
    mutationFn: () => client.templates.create({
        name: name.trim(),
        industry,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(cloneFrom ? { cloneFromCode: cloneFrom } : {}),
      }),
    onSuccess: async (t) => {
      setCreating(false);
      setName("");
      setDescription("");
      await refresh();
      void navigate({ to: "/templates/$code/editor", params: { code: t.code } });
    },
    onError: fail("create theme"),
  });
  const publish = useMutation({
    mutationFn: (code: string) => client.templates.publish({ code }),
    onSuccess: async (r) => {
      toast.success(`Published as version ${r.version}. Stores keep their own customised copy.`);
      await refresh();
    },
    onError: fail("publish theme"),
  });
  const saveDetails = useMutation({
    mutationFn: (v: { code: string; name: string; description: string; industry: string }) =>
      client.templates.updateMeta({ code: v.code, name: v.name.trim(), description: v.description.trim() || null, industry: v.industry }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
    onError: fail("save details"),
  });
  const remove = useMutation({
    mutationFn: (code: string) => client.templates.delete({ code }),
    onSuccess: async () => {
      toast.success("Theme deleted. Stores that already used it keep their own copy.");
      setDeleting(null);
      await refresh();
    },
    onError: fail("delete theme"),
  });
  const setArchived = useMutation({
    mutationFn: (v: { code: string; archived: boolean }) => client.templates.updateMeta(v),
    onSuccess: async (_t, v) => {
      toast.success(v.archived ? "Archived. It is hidden from stores; stores that use it keep their copy." : "Moved back to Drafts.");
      await refresh();
    },
    onError: fail("update theme"),
  });

  if (isLoading) return <PageSkeleton />;

  const visible = (templates ?? []).filter((t) => t.status === tab);

  return (
    <PageContainer size="full">
      <PageHeader
        title="Themes"
        description="Themes stores can pick from their dashboard. Each theme has its own colours, fonts, buttons, corners and layouts for the home, collection, product and cart pages, header and footer. Build it visually, then publish it to the store theme library."
        aside={
          <Button variant="primary" size="sm" onClick={() => { setTab("draft"); setCreating(true); }}>
            <Plus className="mr-1.5 size-3.5" aria-hidden />
            New theme
          </Button>
        }
      />

      <div role="tablist" aria-label="Theme status" className="mb-4 flex gap-1 border-b">
        {TABS.map((t) => {
          const count = (templates ?? []).filter((x) => x.status === t.key).length;
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${on ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {t.label} <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-xs font-normal">{count}</span>
            </button>
          );
        })}
      </div>

      {isError ? (
        <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Could not load themes: {error.message}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState icon={Layers} title={`No ${TABS.find((x) => x.key === tab)?.label.toLowerCase()} themes`} description={TABS.find((x) => x.key === tab)?.empty ?? ""} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="px-4 w-[280px]">Theme</TableHead>
                <TableHead className="w-[120px]">Industry</TableHead>
                <TableHead className="w-[140px]">Code</TableHead>
                <TableHead className="w-[90px]">Version</TableHead>
                <TableHead className="w-[150px]">Status</TableHead>
                <TableHead className="text-right px-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((t) => (
                <TableRow key={t.code} className="hover:bg-muted/30 transition-colors">
                  <TableCell className="px-4">
                    <div className="font-semibold text-sm text-foreground">{t.name}</div>
                    {t.description ? <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5 max-w-sm">{t.description}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs capitalize font-medium">{t.industry}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.code}</TableCell>
                  <TableCell className="font-mono text-xs font-medium">v{t.version}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`inline-flex rounded-full px-2 py-0.5 font-medium ${t.status === "published" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : t.status === "draft" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" : "bg-muted text-muted-foreground"}`}>
                      {t.status === "published" ? "Published" : t.status === "draft" ? "Draft" : "Archived"}
                    </span>
                    {t.status === "published" && t.hasUnpublishedChanges ? (
                      <span className="block mt-1 text-[11px] text-amber-700 dark:text-amber-400 font-medium">Unpublished edits</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="px-4 text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                      <Button size="sm" variant="default" className="h-7 px-2.5 text-xs shrink-0" onClick={() => void navigate({ to: "/templates/$code/editor", params: { code: t.code } })}>
                        <Pencil className="mr-1 size-3" aria-hidden />
                        Edit
                      </Button>
                      <Button size="sm" variant="default" className="h-7 px-2.5 text-xs shrink-0" onClick={() => setEditing({ code: t.code, name: t.name, description: t.description ?? "", industry: t.industry })}>
                        <Settings2 className="mr-1 size-3" aria-hidden />
                        Details
                      </Button>
                      {!t.isActive || t.hasUnpublishedChanges ? (
                        <Button size="sm" variant="primary" className="h-7 px-2.5 text-xs shrink-0" loading={publish.isPending && publish.variables === t.code} onClick={() => publish.mutate(t.code)}>
                          Publish
                        </Button>
                      ) : null}
                      {t.status === "published" ? (
                        <Button size="sm" variant="default" className="h-7 px-2.5 text-xs shrink-0" loading={setArchived.isPending && setArchived.variables?.code === t.code} onClick={() => setArchived.mutate({ code: t.code, archived: true })}>
                          <Archive className="mr-1 size-3" aria-hidden />
                          Archive
                        </Button>
                      ) : null}
                      {t.status === "archived" ? (
                        <Button size="sm" variant="default" className="h-7 px-2.5 text-xs shrink-0" onClick={() => setArchived.mutate({ code: t.code, archived: false })}>
                          Restore
                        </Button>
                      ) : null}
                      {t.status !== "published" ? (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 shrink-0" onClick={() => setDeleting({ code: t.code, name: t.name })}>
                          <Trash2 className="size-3" aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New theme</DialogTitle>
            <DialogDescription>It starts as a draft, hidden from stores until you publish it.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1">
              <Label htmlFor="theme-name">Name</Label>
              <Input id="theme-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="theme-industry">Best for</Label>
              <SimpleSelect
                id="theme-industry"
                value={industry}
                onChange={setIndustry}
                options={INDUSTRIES.map((i) => ({
                  value: i,
                  label: (i[0]?.toUpperCase() ?? "") + i.slice(1),
                }))}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="theme-desc">Short description (shown to stores)</Label>
              <Input id="theme-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="theme-clone">Start from</Label>
              <SimpleSelect
                id="theme-clone"
                value={cloneFrom}
                onChange={setCloneFrom}
                options={[
                  { value: "", label: "Starter layout (all pages)" },
                  ...(templates ?? []).map((t) => ({
                    value: t.code,
                    label: `Copy of ${t.name}`,
                  })),
                ]}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose render={<Button>Cancel</Button>} />
            <Button variant="primary" loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>
              Create and open editor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete theme</DialogTitle>
            <DialogDescription>
              {deleting ? `Permanently delete "${deleting.name}"? Its layouts and draft are removed and cannot be recovered. Stores that already activated it keep their own copy of the pages and settings; they just can no longer pick it from the library.` : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose render={<Button>Cancel</Button>} />
            <Button variant="primary" loading={remove.isPending} onClick={() => deleting && remove.mutate(deleting.code)}>
              Delete theme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Theme details</DialogTitle>
            <DialogDescription>How the theme appears in each store's theme library.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <div className="grid gap-3">
              <div className="grid gap-1">
                <Label htmlFor="edit-name">Name</Label>
                <Input id="edit-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="edit-industry">Best for</Label>
                <SimpleSelect
                  id="edit-industry"
                  value={editing.industry}
                  onChange={(val) => setEditing({ ...editing, industry: val })}
                  options={[...new Set([...INDUSTRIES, editing.industry])].map((i) => ({
                    value: i,
                    label: (i[0]?.toUpperCase() ?? "") + i.slice(1),
                  }))}
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="edit-desc">Short description</Label>
                <Input id="edit-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} maxLength={500} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button>Cancel</Button>} />
            <Button variant="primary" loading={saveDetails.isPending} disabled={!editing?.name.trim()} onClick={() => editing && saveDetails.mutate(editing)}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
